import { t } from 'i18next'
import { useSelector } from 'react-redux'
import {
  profitAndLossArgumentOptionSelector,
  balanceSheetArgumentOptionSelector
} from '../../../../../redux/entities/statementRows/selectors'
import { currentUserCompaniesArgumentOptionsSelector } from '../../../../../redux/session/currentUser/selectors'
import { isGroupCompany } from '../../../../../routes/utils'
import { ContractFranchise } from '../../../../../types/contract/Contract'
import { CustomerGroup } from '../../../../../types/customer/customer'
import { TaskApplication } from '../../../../superuser/types'
import {
  aggregationOptions,
  booleanOptions,
  periodOptions,
  projectSourceOptions,
  receiptsMeasureOptions,
  receiptsSourceOptions,
  routeSourceOptions,
  salesItemMeasureOptions,
  salesItemSourceOptions,
  salesSourceOptions,
  sourceOptions,
  workhourSourceOptions
} from './config'
import { Argument, FormulaFunction, FormulaProduct, WorkType } from './types'
import { useVarArguments } from '../../../../../features/formulas/queries/useVarArguments'
import { Company } from '../../../../../types/company/Company'
import { getAccountOptions } from '../../../../../features/account/queries/getAccountOptions'
import {
  CompanyGroup,
  getCompanyGroupOptions
} from '../../../../../features/companyGroup/queries/getCompanyGroupOptions'
import { getDimensionOptions } from '../../../../../features/dimension/queries/getDimensionOptions'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import formulasEndpoints from '../../../../../features/formulas/api/formulasEndpoints'
import formulaQueryKeys from '../../../../../features/formulas/queries/formulaQueryKeys'
import { Formula } from '../../../../../features/formulas/types/Formula'
import { getFormulaOptions } from '../../../../../features/formulas/queries/getFormulaOptions'
import { getBudgetingScenarioOptions } from '../../../../../features/scenario/queries/budgetingScenario/getBudgetingScenarioOptions'
import { getForecastOptions } from '../../../../../features/scenario/queries/forecast/getForecastOptions'
import { BudgetingScenario } from '../../../../../features/scenario/types/BudgetingScenario'
import { appLanguageSelector } from '../../../../../redux/app/selectors'
import { translateRecord } from '../../../../../utils/helpers'
import { Account } from '../../../../../features/account/types/Account'
import { flattenGroupWithSubgroups } from '../../../../../features/companyGroup/utils/companyGroupUtils'

const companyGroupToOptions = (group: CompanyGroup) =>
  flattenGroupWithSubgroups(group as Company).map((company: Company) => ({
    name: `${company.id} - ${company.name}`,
    value: `'${company.id}'`
  }))

const companyGroupOptionsQuery = (context: { companyId: string }) =>
  getCompanyGroupOptions(context.companyId, companyGroupToOptions)

const companyGroupArgument: Argument = {
  label: 'company_id',
  example: "'29091575'",
  optionsConfig: {
    query: companyGroupOptionsQuery
  }
}

export const useFunctions = () => {
  const { data: varArgumentOptions } = useVarArguments()
  const language = useSelector(appLanguageSelector)

  const functions: {
    [key: string]: FormulaFunction
  } = {
    account: {
      name: 'account',
      arguments: [
        {
          label: 'code',
          example: '[3000, 3001, 3002]',
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getAccountOptions(context.companyId, language, (accounts: Account[]) =>
                accounts.map(account => {
                  const translatedAccount = translateRecord(account, language)
                  return {
                    name: `${translatedAccount.code} - ${translatedAccount.name}`,
                    value: translatedAccount.code
                  }
                })
              )
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    group_account: {
      name: 'group_account',
      authority: {
        custom: isGroupCompany
      },
      arguments: [
        companyGroupArgument,
        {
          label: 'code',
          example: '[3000, 3001, 3002]',
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getAccountOptions(context.companyId, language, (accounts: Account[]) =>
                accounts.map(account => {
                  const translatedAccount = translateRecord(account, language)
                  return {
                    name: `${translatedAccount.code} - ${translatedAccount.name}`,
                    value: translatedAccount.code
                  }
                })
              )
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    global_ref: {
      name: 'global_ref',
      authority: {
        custom: isGroupCompany
      },
      arguments: [
        {
          label: 'company_id',
          example: "'29091575'",
          optionsConfig: {
            optionsSelector: currentUserCompaniesArgumentOptionsSelector
          }
        },
        {
          label: 'code',
          example: `'liikevaihto-kum'`,
          optionsConfig: {
            request: {
              url: formulasEndpoints.list,
              queryKey: formulaQueryKeys.list,
              select: (formulas: Formula[]) => formulas.filter(f => f.isVariable === false),
              dataTransform: (formula: Formula) => ({
                name: formula.name,
                value: `'${formula.code}'`
              })
            }
          }
        }
      ]
    },
    pl: {
      name: 'pl',
      arguments: [
        {
          label: 'key',
          example: '5',
          optionsConfig: {
            optionsSelector: profitAndLossArgumentOptionSelector
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    bs: {
      name: 'bs',
      arguments: [
        {
          label: 'key',
          example: '[58, 59]',
          optionsConfig: {
            optionsSelector: balanceSheetArgumentOptionSelector
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: {
            options: periodOptions
          }
        }
      ]
    },
    ref: {
      name: 'ref',
      arguments: [
        {
          label: 'code',
          example: "['code1', 'code2']",
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getFormulaOptions(context.companyId, language, (formulas: Formula[]) =>
                formulas
                  .filter(f => f.isVariable === false)
                  .map(formula => ({
                    name: formula.name,
                    value: `'${formula.code}'`
                  }))
              )
          }
        }
      ]
    },
    gs: {
      name: 'gs',
      arguments: [
        {
          label: 'code',
          example: "'code'",
          optionsByPrevArgument: {
            default: {
              request: {
                url: `/companies/{companyId}/integrations/google/sheets/external`,
                dataTransform: sheet => ({
                  name: sheet.code,
                  value: `'${sheet.code}'`
                })
              }
            }
          }
        },
        { label: 'key', example: '2' },
        {
          label: 'aggregation function',
          example: 'lastValue',
          optionsConfig: {
            options: aggregationOptions
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: {
            options: periodOptions
          }
        }
      ]
    },
    onedrive: {
      name: 'onedrive',
      arguments: [
        {
          label: 'code',
          example: "'code'",
          optionsByPrevArgument: {
            default: {
              request: {
                url: `/onedrive/sheets/{companyId}/external`,
                requestType: 'integration',
                dataTransform: sheet => ({
                  name: sheet.code,
                  value: `'${sheet.code}'`
                })
              }
            }
          }
        },
        { label: 'key', example: '2' },
        {
          label: 'aggregation function',
          example: 'lastValue',
          optionsConfig: {
            options: aggregationOptions
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: {
            options: periodOptions
          }
        }
      ]
    },
    div: {
      name: 'div',
      arguments: [
        { label: 'numerator', example: 'pl(25, current)' },
        { label: 'denominator', example: 'pl(1, current)' },
        { label: 'expression if denominator is 0', example: '0' }
      ]
    },
    delta: {
      name: 'delta',
      arguments: [
        {
          label: 'source',
          example: 'account',
          optionsConfig: {
            options: sourceOptions
          }
        },
        {
          label: 'key',
          example: '[2971, 2972]',
          optionsByPrevArgument: {
            pl: {
              optionsSelector: profitAndLossArgumentOptionSelector
            },
            bs: {
              optionsSelector: balanceSheetArgumentOptionSelector
            },
            account: {
              query: (context: { companyId: string }) =>
                getAccountOptions(context.companyId, language, (accounts: Account[]) =>
                  accounts.map(account => ({
                    name: `${account.code} - ${account.name}`,
                    value: account.code
                  }))
                )
            }
          }
        },
        {
          label: 'inversed',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        }
      ]
    },
    growth: {
      name: 'growth',
      arguments: [
        {
          label: 'source',
          example: 'account',
          optionsConfig: {
            options: sourceOptions
          }
        },
        {
          label: 'key',
          example: '[3000, 3200]',
          optionsByPrevArgument: {
            pl: {
              optionsSelector: profitAndLossArgumentOptionSelector
            },
            bs: {
              optionsSelector: balanceSheetArgumentOptionSelector
            },
            account: {
              // optionsSelector: companyAccountsArgumentOptionsSelector,
              query: (context: { companyId: string }) =>
                getAccountOptions(context.companyId, language, (accounts: Account[]) =>
                  accounts.map(account => ({
                    name: `${account.code} - ${account.name}`,
                    value: account.code
                  }))
                )
            }
          }
        }
      ]
    },
    if: {
      name: 'if',
      arguments: [
        { label: 'logical expression', example: '1 = 1' },
        { label: 'expression if true', example: '1' },
        { label: 'expression if false', example: '0' }
      ]
    },
    max: {
      name: 'max',
      arguments: [
        { label: 'expression', example: '1' },
        { label: 'expression', example: '2' },
        { label: '...', example: '3' }
      ]
    },
    min: {
      name: 'min',
      arguments: [
        { label: 'expression', example: '1' },
        { label: 'expression', example: '2' },
        { label: '...', example: '3' }
      ]
    },
    receipts: {
      name: 'receipts',
      arguments: [
        {
          label: 'source',
          example: 'restolution',
          optionsConfig: {
            options: receiptsSourceOptions
          }
        },
        {
          label: 'measurement',
          example: 'quantity',
          optionsConfig: {
            options: receiptsMeasureOptions
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: {
            options: periodOptions
          }
        }
      ]
    },
    sales: {
      name: 'sales',
      arguments: [
        {
          label: 'source',
          example: 'kotipizza',
          optionsConfig: {
            options: salesSourceOptions
          }
        },
        {
          label: 'measurement',
          example: 'amount',
          optionsConfig: {
            options: salesItemMeasureOptions
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: {
            options: periodOptions
          }
        }
      ]
    },
    product: {
      name: 'product',
      arguments: [
        {
          label: 'source',
          example: 'accounting',
          optionsConfig: { options: salesItemSourceOptions }
        },
        {
          label: 'key',
          example: `['p-1', 'p-2']`,
          optionsByPrevArgument: {
            accounting: {
              request: {
                url: `/reporting/products/accounting`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            futursoft: {
              request: {
                url: `/reporting/products/futursoft`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            kotipizza: {
              request: {
                url: `/reporting/products/kotipizza`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            restolution: {
              request: {
                url: `/reporting/products/restolution`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            tulopos: {
              request: {
                url: `/reporting/products/tulopos`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            simphony: {
              request: {
                url: `/reporting/products/simphony`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            }
          }
        },
        {
          label: 'measurement',
          example: 'amount',
          optionsConfig: { options: salesItemMeasureOptions }
        },
        {
          label: 'calculate_accruals',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    product_group: {
      name: 'product_group',
      arguments: [
        {
          label: 'source',
          example: 'accounting',
          optionsConfig: { options: salesItemSourceOptions }
        },
        {
          label: 'key',
          example: `'g-1'`,
          optionsByPrevArgument: {
            accounting: {
              request: {
                url: `/reporting/products/groups/accounting`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            futursoft: {
              request: {
                url: `/reporting/products/groups/futursoft`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            kotipizza: {
              request: {
                url: `/reporting/products/groups/kotipizza`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            restolution: {
              request: {
                url: `/reporting/products/groups/restolution`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            tulopos: {
              request: {
                url: `/reporting/products/groups/tulopos`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            simphony: {
              request: {
                url: `/reporting/products/groups/simphony`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            }
          }
        },
        {
          label: 'measurement',
          example: 'amount',
          optionsConfig: { options: salesItemMeasureOptions }
        },
        {
          label: 'calculate_accruals',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    product_family: {
      name: 'product_family',
      arguments: [
        {
          label: 'source',
          example: 'kotipizza',
          optionsConfig: {
            options: [
              {
                name: 'futursoft',
                value: 'futursoft'
              },
              {
                name: 'kotipizza',
                value: 'kotipizza',
                authority: {
                  franchise: ContractFranchise.KOTIPIZZA
                }
              },
              {
                name: 'restolution',
                value: 'restolution'
              },
              {
                name: 'tulopos',
                value: 'tulopos'
              },
              {
                name: 'simphony',
                value: 'simphony'
              }
            ]
          }
        },
        {
          label: 'key',
          example: `'f-1'`,
          optionsByPrevArgument: {
            futursoft: {
              request: {
                url: `/reporting/products/families/futursoft`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            kotipizza: {
              request: {
                url: `/reporting/products/families/kotipizza`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            restolution: {
              request: {
                url: `/reporting/products/families/restolution`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            tulopos: {
              request: {
                url: `/reporting/products/families/tulopos`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            },
            simphony: {
              request: {
                url: `/reporting/products/families/simphony`,
                requestType: 'integration',
                dataTransform: (product: FormulaProduct) => ({
                  name: `${product.id} - ${product.name}`,
                  value: `'${product.id}'`
                })
              }
            }
          }
        },
        {
          label: 'measurement',
          example: 'amount',
          optionsConfig: { options: salesItemMeasureOptions }
        },
        {
          label: 'calculate_accruals',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    customer: {
      name: 'customer',
      authority: {
        accountingSoftware: [TaskApplication.NETVISOR, TaskApplication.PROCOUNTOR, TaskApplication.FENNOA]
      },
      arguments: [
        {
          label: 'source',
          example: 'accounting',
          optionsConfig: {
            options: [
              {
                name: 'accounting',
                value: 'accounting'
              }
            ]
          }
        },
        {
          label: 'key',
          example: `['p-1', 'p-2']`,
          optionsByPrevArgument: {
            accounting: {
              request: {
                url: `/reporting/customers/accounting`,
                requestType: 'integration',
                dataTransform: (customer: CustomerGroup) => ({
                  name: `${customer.id} - ${customer.name}`,
                  value: `'${customer.id}'`
                })
              }
            }
          }
        },
        {
          label: 'calculate_accruals',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    customer_group: {
      name: 'customer_group',
      authority: {
        accountingSoftware: [TaskApplication.NETVISOR, TaskApplication.PROCOUNTOR]
      },
      arguments: [
        {
          label: 'source',
          example: 'accounting',
          optionsConfig: {
            options: [
              {
                name: 'accounting',
                value: 'accounting'
              }
            ]
          }
        },
        {
          label: 'key',
          example: `['p-1', 'p-2']`,
          optionsByPrevArgument: {
            accounting: {
              request: {
                url: `/reporting/customers/groups/accounting`,
                requestType: 'integration',
                dataTransform: (customer: CustomerGroup) => ({
                  name: `${customer.id} - ${customer.name}`,
                  value: `'${customer.id}'`
                })
              }
            }
          }
        },
        {
          label: 'calculate_accruals',
          example: 'false',
          optionsConfig: { options: booleanOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    route: {
      name: 'route',
      arguments: [
        {
          label: 'source',
          example: 'mapon',
          optionsConfig: { options: routeSourceOptions }
        },
        {
          label: 'measurement',
          example: 'distance',
          optionsByPrevArgument: {
            ac_panther: {
              options: [
                { name: 'consumption', value: 'consumption' },
                { name: 'distance', value: 'distance' },
                { name: 'duration', value: 'duration' },
                { name: 'idle', value: 'idle' },
                { name: 'park', value: 'park' }
              ]
            },
            kiho: {
              options: [
                { name: 'distance', value: 'distance' },
                { name: 'duration', value: 'duration' }
              ]
            },
            mapon: {
              options: [
                { name: 'consumption', value: 'consumption' },
                { name: 'distance', value: 'distance' },
                { name: 'duration', value: 'duration' }
              ]
            },
            logisystems: {
              options: [
                { name: 'distance', value: 'distance' },
                { name: 'duration', value: 'duration' },
                { name: 'income', value: 'income' }
              ]
            }
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    workhour: {
      name: 'workhour',
      arguments: [
        {
          label: 'source',
          example: 'maraplan',
          optionsConfig: { options: workhourSourceOptions }
        },
        {
          label: 'type',
          example: 'work',
          optionsByPrevArgument: {
            maraplan: {
              request: {
                url: `/reporting/workhours/types/maraplan`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            ontime: {
              request: {
                url: `/reporting/workhours/types/ontime`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            nepton: {
              request: {
                url: `/reporting/workhours/types/nepton`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            hrsuunti: {
              request: {
                url: `/reporting/workhours/types/hrsuunti`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            työvuorovelho: {
              /** Tarkoituksella maraplan, koska se on sama järjestelmä */
              request: {
                url: `/reporting/workhours/types/maraplan`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            severa: {
              request: {
                url: `/reporting/workhours/types/severa`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            },
            tamigo: {
              request: {
                url: `/reporting/workhours/types/tamigo`,
                requestType: 'integration',
                dataTransform: (type: WorkType) => ({
                  name: `${type.name}`,
                  value: `'${type.id}'`
                })
              }
            }
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    project: {
      name: 'project',
      arguments: [
        {
          label: 'source',
          example: 'severa',
          optionsConfig: { options: projectSourceOptions }
        },
        {
          label: 'property',
          example: 'expected_value',
          optionsByPrevArgument: {
            severa: {
              options: [
                { name: 'estimate', value: 'estimate' },
                { name: 'expected_value', value: 'expected_value' }
              ]
            }
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    variable: {
      name: 'variable',
      arguments: [
        {
          label: 'code',
          example: "['code1', 'code2']",
          optionsConfig: {
            options: varArgumentOptions
          }
        },
        {
          label: 'aggregation function',
          example: 'lastValue',
          optionsConfig: { options: aggregationOptions }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    accumulate: {
      name: 'accumulate',
      arguments: [{ label: 'expression', example: 'account(3000, current)' }]
    },
    account_entries: {
      name: 'account_entries',
      arguments: [
        {
          label: 'account',
          example: '[3000, 3001, 3002]',
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getAccountOptions(context.companyId, language, (accounts: Account[]) =>
                accounts.map(account => {
                  const translatedAccount = translateRecord(account, language)
                  return {
                    name: `${translatedAccount.code} - ${translatedAccount.name}`,
                    value: translatedAccount.code
                  }
                })
              )
          }
        },
        {
          label: 'voucher_type',
          example: 'all',
          optionsConfig: {
            options: [
              {
                name: 'all',
                value: 'all'
              }
            ],
            request: {
              url: `/reporting/vouchers/types`,
              requestType: 'integration',
              dataTransform: (type: string) => ({
                name: `${type}`,
                value: `'${type}'`
              })
            }
          }
        },
        {
          label: 'description',
          example: 'description',
          optionsConfig: {
            options: [
              {
                name: 'all',
                value: 'all'
              }
            ]
          }
        },
        {
          label: 'type',
          example: 'all',
          optionsConfig: {
            options: [
              {
                name: 'debit',
                value: 'debit'
              },
              {
                name: 'credit',
                value: 'credit'
              },
              {
                name: 'all',
                value: 'all'
              }
            ]
          }
        },
        {
          label: 'period',
          example: 'current',
          optionsConfig: { options: periodOptions }
        }
      ]
    },
    use_dimension: {
      name: 'use_dimension',
      arguments: [
        {
          label: 'dimension_id',
          example: '32487234',
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getDimensionOptions(context.companyId, (dimensions: Dimension[]) =>
                dimensions.map(dimension => ({
                  name: `${dimension.dimensionId} - ${dimension.name}`,
                  value: `'${dimension.dimensionId}'`
                }))
              )
          }
        },
        { label: 'expression', example: 'account(3000, current)' }
      ]
    },
    use_company: {
      name: 'use_company',
      authority: {
        custom: isGroupCompany
      },
      arguments: [companyGroupArgument, { label: 'expression', example: 'account(3000, current)' }]
    },
    eliminate: {
      name: 'eliminate',
      authority: {
        custom: isGroupCompany
      },
      arguments: [companyGroupArgument, { label: 'expression', example: 'account(3000, current)' }]
    },
    use_actuals: {
      name: 'use_actuals',
      arguments: [{ label: 'expression', example: 'account(3000, current)' }]
    },
    use_budget: {
      name: 'use_budget',
      arguments: [
        {
          label: 'scenario',
          example: 'default',
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getBudgetingScenarioOptions(context.companyId, (scenarios: BudgetingScenario[]) =>
                scenarios.map(scenario => ({
                  name: scenario.name || t('global:default'),
                  value: `'${scenario.code}'`
                }))
              )
          }
        },
        { label: 'expression', example: 'account(3000, current)' }
      ]
    },
    use_forecast: {
      name: 'use_forecast',
      arguments: [
        {
          label: 'scenario_code',
          example: "'forecast_2024'",
          optionsConfig: {
            query: (context: { companyId: string }) =>
              getForecastOptions(context.companyId, (scenarios: BudgetingScenario[]) =>
                scenarios.map(scenario => ({
                  name: scenario.name || t('global:default'),
                  value: `'${scenario.code}'`
                }))
              )
          }
        },
        { label: 'expression', example: 'account(3000, current)' }
      ]
    },
    use_offset: {
      name: 'use_offset',
      arguments: [
        {
          label: 'offset',
          example: '-12',
          optionsConfig: {
            options: [
              { name: '-24', value: '-24' },
              { name: '-12', value: '-12' },
              { name: '-6', value: '-6' },
              { name: '-3', value: '-3' },
              { name: '-1', value: '-1' },
              { name: '1', value: '1' },
              { name: '3', value: '3' },
              { name: '6', value: '6' },
              { name: '12', value: '12' },
              { name: '24', value: '24' }
            ]
          }
        },
        {
          label: 'unit',
          example: 'month',
          optionsConfig: {
            options: [
              { name: 'year', value: 'year' },
              { name: 'month', value: 'month' }
            ]
          }
        },
        { label: 'expression', example: 'account(3000, current)' }
      ]
    }
  }
  return functions
}
